import { Absence, Employee } from '../types';
import { getLocalDateTimestamp, parseLocalDateYmd } from './dateFormat';

/**
 * Extrai ano e mês (0-indexed) de string de data ISO ou YYYY-MM-DD.
 * @param dateStr - Data em formato reconhecível
 * @returns Tupla [ano, mês] ou null se inválida
 */
export function parseAbsenceYearMonth(dateStr: string): [number, number] | null {
  const parsed = parseLocalDateYmd(dateStr);
  if (!parsed) return null;
  return [parsed.getFullYear(), parsed.getMonth()];
}

/**
 * Verifica se a falta pertence ao mês/ano da data de referência.
 * @param dateStr - Data da falta (YYYY-MM-DD ou ISO)
 * @param referenceDate - Dia usado para definir o mês corrente
 */
export function isAbsenceInReferenceMonth(dateStr: string, referenceDate: Date): boolean {
  const parts = parseAbsenceYearMonth(dateStr);
  if (!parts) return false;
  return parts[0] === referenceDate.getFullYear() && parts[1] === referenceDate.getMonth();
}

/**
 * Filtra faltas que caem no mês da data de referência.
 * @param absences - Lista de faltas do colaborador
 * @param referenceDate - Dia de referência (ex.: relógio sincronizado)
 */
export function filterAbsencesInMonth(absences: Absence[] | undefined, referenceDate: Date): Absence[] {
  return (absences ?? []).filter((absence) => isAbsenceInReferenceMonth(absence.date, referenceDate));
}

/**
 * Conta faltas do colaborador no mês da data de referência.
 * @param employee - Colaborador
 * @param referenceDate - Dia de referência
 */
export function countEmployeeAbsencesInMonth(employee: Employee, referenceDate: Date): number {
  return filterAbsencesInMonth(employee.absences, referenceDate).length;
}

/**
 * Indica se o colaborador tem ao menos uma falta no mês corrente.
 * @param employee - Colaborador
 * @param referenceDate - Dia de referência
 */
export function hasAbsencesInMonth(employee: Employee, referenceDate: Date): boolean {
  return countEmployeeAbsencesInMonth(employee, referenceDate) > 0;
}

/**
 * Ordena faltas da mais recente para a mais antiga.
 * @param absences - Lista de faltas
 */
export function sortAbsencesByDateDesc(absences: Absence[]): Absence[] {
  return [...absences].sort(
    (a, b) => getLocalDateTimestamp(b.date) - getLocalDateTimestamp(a.date),
  );
}

/** Grupo de faltas agrupadas por mês/ano. */
export interface AbsenceMonthGroup {
  key: string;
  label: string;
  absences: Absence[];
}

const MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/**
 * Formata rótulo de mês/ano para exibição (ex.: "Junho 2026").
 * @param year - Ano calendário
 * @param month - Mês 0-indexed
 */
export function formatMonthYearLabel(year: number, month: number): string {
  return `${MONTH_LABELS[month] ?? 'Mês'} ${year}`;
}

/**
 * Agrupa faltas por mês/ano, do mais recente ao mais antigo.
 * @param absences - Lista de faltas
 */
export function groupAbsencesByMonth(absences: Absence[]): AbsenceMonthGroup[] {
  const grouped = new Map<string, AbsenceMonthGroup>();

  for (const absence of sortAbsencesByDateDesc(absences)) {
    const parts = parseAbsenceYearMonth(absence.date);
    if (!parts) continue;

    const [year, month] = parts;
    const key = `${year}-${String(month + 1).padStart(2, '0')}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        label: formatMonthYearLabel(year, month),
        absences: [],
      });
    }

    grouped.get(key)!.absences.push(absence);
  }

  return Array.from(grouped.values()).sort((a, b) => b.key.localeCompare(a.key));
}

/**
 * Conta total de registros de falta no mês/ano informado (todos os colaboradores).
 * @param employees - Lista de colaboradores
 * @param year - Ano calendário
 * @param month - Mês 0-indexed
 */
export function countAbsencesInMonth(
  employees: Employee[],
  year: number,
  month: number,
): number {
  let total = 0;
  for (const emp of employees) {
    for (const absence of emp.absences ?? []) {
      const parts = parseAbsenceYearMonth(absence.date);
      if (parts !== null && parts[0] === year && parts[1] === month) {
        total += 1;
      }
    }
  }
  return total;
}
